import { mvpApiGetPlanogramOptions } from "@labeling-tools/api/atlas/@tanstack/react-query.gen";
import { useQuery } from "@tanstack/react-query";
import { ActivePlanoProductPopover } from "./active-label-popover/active-plano-product-popover.comp";
import { PlanoLabelBoxes } from "./plano-label-boxes/plano-label-boxes.comp";
import { useActivePlanoProductInfoStore } from "./use-active-plano-product-info-store";
import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
  useTransformEffect,
} from "react-zoom-pan-pinch";
import {
  getLabelDimension,
  getPlanoDimension,
} from "../utils/plano-related.utils";
import { PlanogramMinimap } from "./planogram-minimap.comp";
import { PlanogramSearch } from "./planogram-search/planogram-search.comp";
import { fuzzyMatch, normalizeText } from "../utils/fuzzy-match";

/** Bridge component that sits inside TransformWrapper and syncs transform state up */
const TransformSync = ({
  onStateChange,
}: {
  onStateChange: (state: {
    scale: number;
    positionX: number;
    positionY: number;
  }) => void;
}) => {
  useTransformEffect(({ state }) => {
    onStateChange(state);
  });
  return null;
};

interface Props {
  planogram_id?: string;
  /** If provided, will auto-highlight and zoom to this product when the planogram loads */
  initialProductId?: string | null;
  hidePopover?: boolean;
  enable_product_id_copy?: boolean;
  /** Product IDs to highlight as selected (for multi-select support) */
  selectedProductIds?: string[];
  /** Product IDs to highlight with a warning (yellow) border */
  warnedProductIds?: string[];
  /** Product IDs to transiently highlight (teal) — e.g. on shelf-detection hover. Never pans. */
  highlightedProductIds?: string[];
  /** Product IDs defining a region to zoom into on load (used when no initialProductId) */
  zoomToProductIds?: string[];
  /** Override image URLs for product boxes (keyed by planogram product id) */
  imageUrlOverrides?: Record<string, string>;
  /** Override image URLs for hover tooltips (keyed by planogram product id) */
  tooltipImageUrlOverrides?: Record<string, string>;
  /** Auto-zoom on load so the planogram content fills the container height */
  fitContent?: boolean;
  /** Skip the pre-stitched background image even when image_url is available */
  disableBackgroundImage?: boolean;
  /** Zoom to initialProductId without selecting/highlighting it */
  zoomOnlyInitialProduct?: boolean;
  /** Use a wider zoom when focusing on initialProductId (6× padding instead of 3×) */
  wideInitialZoom?: boolean;
  /**
   * When true, briefly "ping" the selected boxes each time the viewer frames
   * them (i.e. when `zoomToProductIds` changes and matches products). Opt-in so
   * consumers that don't want the animation are unaffected.
   */
  pulseOnFrame?: boolean;
  /**
   * Bump this number to re-run the fit (re-center/zoom to content) — i.e. reset
   * the view. Lets a consumer wire a "reset view" control/hotkey to the viewer.
   */
  resetViewNonce?: number;
  /**
   * Receives the internal react-zoom-pan-pinch controller once the viewer has
   * mounted (and `null` on unmount), so a consumer can drive zoom/pan
   * imperatively — e.g. wire zoom hotkeys to the planogram.
   */
  onTransformRef?: (ref: ReactZoomPanPinchRef | null) => void;
}

export const PlanogramViewer = (props: Props) => {
  const {
    planogram_id,
    initialProductId,
    hidePopover = false,
    enable_product_id_copy = false,
    selectedProductIds,
    warnedProductIds,
    highlightedProductIds,
    zoomToProductIds,
    imageUrlOverrides,
    tooltipImageUrlOverrides,
    fitContent = false,
    disableBackgroundImage = false,
    zoomOnlyInitialProduct = false,
    wideInitialZoom = false,
    pulseOnFrame = false,
    resetViewNonce,
    onTransformRef,
  } = props;
  // Bumped each time the fit effect frames selected products; drives the
  // one-time "ping" animation on the matching red boxes (see pulseOnFrame).
  const [pulseNonce, setPulseNonce] = useState(0);

  // Fall back to selectedProductIds when no explicit zoom target is set.
  // Memoize by sorted join so the effect doesn't re-fire on every render.
  const rawZoomIds = zoomToProductIds ?? selectedProductIds;
  const effectiveZoomToProductIds = useMemo(
    () => rawZoomIds,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawZoomIds?.slice().sort().join(",")],
  );
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const [currentScale, setCurrentScale] = useState(1);
  const [transformState, setTransformState] = useState({
    positionX: 0,
    positionY: 0,
    scale: 1,
  });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: planogramResp } = useQuery({
    ...mvpApiGetPlanogramOptions({
      path: { planogram_id: planogram_id || "" },
    }),
    enabled: !!planogram_id,
    staleTime: 30_000,
  });

  // Surface the transform controller once the viewer has mounted (the
  // TransformWrapper only renders once the planogram has loaded), and clear it
  // on unmount/reload so a consumer never drives a stale controller.
  useEffect(() => {
    if (!onTransformRef) return;
    onTransformRef(planogramResp ? transformRef.current : null);
    return () => onTransformRef(null);
  }, [planogramResp, onTransformRef]);

  // Background image: use the planogram's pre-stitched image when available,
  // unless imageUrlOverrides are active (e.g. Kroger per-box overrides) or disabled explicitly.
  const backgroundImageUrl = useMemo(() => {
    if (disableBackgroundImage) return null;
    if (imageUrlOverrides) return null;
    return planogramResp?.image_url ?? null;
  }, [disableBackgroundImage, imageUrlOverrides, planogramResp?.image_url]);

  // Search
  const [searchQuery, setSearchQuery] = useState("");
  // Reset search when planogram changes
  useEffect(() => {
    setSearchQuery("");
  }, [planogram_id]);

  const searchTexts = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of planogramResp?.products ?? []) {
      const parts = [
        p.description,
        p.override_description,
        typeof p.metadata?.supplier === "string" ? p.metadata.supplier : "",
        typeof p.metadata?.category === "string" ? p.metadata.category : "",
        typeof p.metadata?.subcategory === "string"
          ? p.metadata.subcategory
          : "",
      ].filter(Boolean);
      map.set(p.id, normalizeText(parts.join(" ")));
    }
    return map;
  }, [planogramResp?.products]);

  const searchMatchedIds = useMemo<Set<string> | undefined>(() => {
    if (!searchQuery.trim()) return undefined;
    const matched = new Set<string>();
    for (const [id, text] of searchTexts) {
      if (fuzzyMatch(searchQuery, text)) matched.add(id);
    }
    return matched;
  }, [searchQuery, searchTexts]);

  const activePlanogramProductId = useActivePlanoProductInfoStore(
    (s) => s.productBoxInfo.planogram_product_id,
  );

  const setActiveLabelBoxInfo = useActivePlanoProductInfoStore(
    (s) => s.setActiveLabelBoxInfo,
  );

  const handleTransformChange = (state: {
    scale: number;
    positionX: number;
    positionY: number;
  }) => {
    setCurrentScale(state.scale);
    setTransformState(state);
  };

  const handleMinimapPan = useCallback(
    (positionX: number, positionY: number) => {
      transformRef.current?.setTransform(
        positionX,
        positionY,
        transformState.scale,
        0,
      );
    },
    [transformState.scale],
  );

  const handleTransformed = (
    _ref: ReactZoomPanPinchRef,
    state: { scale: number; positionX: number; positionY: number },
  ) => {
    handleTransformChange(state);
  };

  // Track container size — re-run when planogramResp arrives since the
  // container div doesn't mount until planogramResp is non-null.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setContainerSize({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [!!planogramResp]);

  // Auto-highlight and zoom to initial product when planogram loads
  useEffect(() => {
    if (!planogramResp || !initialProductId) return;

    const planogramProducts = planogramResp.products ?? [];
    const targetProduct = planogramProducts.find(
      (p) => p.id === initialProductId || p.product_id === initialProductId,
    );

    if (!targetProduct) return;

    // Calculate dimensions
    const planoDimension = getPlanoDimension(planogramProducts);
    const labelDimension = getLabelDimension(targetProduct);

    // Calculate position as percentage (same as OneLabelBox)
    const leftPercent =
      (labelDimension.x0 - planoDimension.x0) / planoDimension.w;
    const topPercent =
      (labelDimension.y0 - planoDimension.y0) / planoDimension.h;
    const widthPercent = labelDimension.w / planoDimension.w;
    const heightPercent = labelDimension.h / planoDimension.h;

    // Calculate center of the product for zoom target
    const centerX = leftPercent + widthPercent / 2;
    const centerY = topPercent + heightPercent / 2;

    // Use setTimeout to ensure TransformWrapper has mounted and ref is available
    let highlightTimeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutId = setTimeout(() => {
      const container = document.getElementById("planogram-view-container");
      if (!container || !transformRef.current) return;

      const containerRect = container.getBoundingClientRect();
      const cw = containerRect.width;
      const ch = containerRect.height;

      // Compute the actual content size (aspect-ratio-constrained inner div)
      const planoAR = planoDimension.w / planoDimension.h;
      const containerAR = cw / ch;
      let contentW: number, contentH: number, offsetX: number, offsetY: number;
      if (planoAR >= containerAR) {
        contentW = cw;
        contentH = cw / planoAR;
        offsetX = 0;
        offsetY = (ch - contentH) / 2;
      } else {
        contentH = ch;
        contentW = ch * planoAR;
        offsetX = (cw - contentW) / 2;
        offsetY = 0;
      }

      // Calculate zoom scale (fit product with some padding)
      const zoomPadding = wideInitialZoom ? 6 : 3;
      const scaleX = cw / (widthPercent * contentW * zoomPadding);
      const scaleY = ch / (heightPercent * contentH * zoomPadding);
      const scale = Math.min(scaleX, scaleY, 5); // Cap at 5x zoom

      // Calculate the position to center the product in container
      const pixelCenterX = offsetX + centerX * contentW;
      const pixelCenterY = offsetY + centerY * contentH;
      const positionX = cw / 2 - pixelCenterX * scale;
      const positionY = ch / 2 - pixelCenterY * scale;

      // Apply zoom and pan
      transformRef.current.setTransform(
        positionX,
        positionY,
        scale,
        300,
        "easeOut",
      );

      // Eagerly update transform state so minimap renders during animation
      handleTransformChange({ scale, positionX, positionY });

      // Set active product info for highlighting/popover (skipped in zoom-only mode)
      if (!zoomOnlyInitialProduct) {
        highlightTimeoutId = setTimeout(() => {
          // Don't clobber a product the labeler already clicked while we were
          // waiting to auto-highlight the model's suggestion — their click
          // should win, not get silently reverted a moment later.
          if (
            useActivePlanoProductInfoStore.getState().productBoxInfo
              .planogram_product_id
          ) {
            return;
          }
          setActiveLabelBoxInfo({
            left: offsetX + leftPercent * contentW,
            top: offsetY + topPercent * contentH,
            planogram_product_id: targetProduct.id,
            product_id: targetProduct.product_id,
          });
        }, 350);
      }
    }, 100); // Small delay to ensure TransformWrapper mounts

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(highlightTimeoutId);
    };
  }, [
    planogramResp,
    initialProductId,
    setActiveLabelBoxInfo,
    zoomOnlyInitialProduct,
    wideInitialZoom,
  ]);

  // Auto-zoom to fit planogram content to container on load.
  // If selectedProductIds are available, vertically centers on those products
  // instead of centering the whole planogram.
  useEffect(() => {
    if (!planogramResp || !fitContent || initialProductId) return;

    const planogramProducts = planogramResp.products ?? [];
    const planoDimension = getPlanoDimension(planogramProducts);
    if (!planoDimension.w || !planoDimension.h) return;

    const timeoutId = setTimeout(() => {
      const container = document.getElementById("planogram-view-container");
      if (!container || !transformRef.current) return;

      const containerRect = container.getBoundingClientRect();
      const cw = containerRect.width;
      const ch = containerRect.height;
      if (!cw || !ch) return;

      const planoAR = planoDimension.w / planoDimension.h;
      const containerAR = cw / ch;

      // Determine how the planogram is laid out inside the container
      // (PlanoLabelBoxes fits content preserving aspect ratio)
      let contentW: number;
      let contentH: number;
      if (planoAR >= containerAR) {
        // Planogram is wider than container — content fills width, letterboxed vertically
        contentW = cw;
        contentH = cw / planoAR;
      } else {
        // Planogram is taller than container — content fills height, pillarboxed horizontally
        contentH = ch;
        contentW = ch * planoAR;
      }

      const offsetX = (cw - contentW) / 2;
      const offsetY = (ch - contentH) / 2;

      // Contain-fit so the WHOLE planogram is visible (min ratio), backed off
      // 0.9 for a margin. Labelers zoom in themselves for detail (the minimap
      // appears once they do); selections are surfaced via highlight + pulse
      // rather than by moving the view.
      const scale = Math.min(cw / contentW, ch / contentH) * 0.9;
      const positionX = cw / 2 - (offsetX + contentW / 2) * scale;
      const positionY = ch / 2 - (offsetY + contentH / 2) * scale;

      // A selection is present → ping it so it's easy to spot in the full view.
      const didFrameSelection =
        !!effectiveZoomToProductIds?.length &&
        planogramProducts.some(
          (p) =>
            effectiveZoomToProductIds.includes(p.id) ||
            effectiveZoomToProductIds.includes(p.product_id),
        );

      transformRef.current.setTransform(
        positionX,
        positionY,
        scale,
        300,
        "easeOut",
      );
      handleTransformChange({ scale, positionX, positionY });

      // Fire the "ping" only once we've actually framed a selection.
      if (pulseOnFrame && didFrameSelection) {
        setPulseNonce((n) => n + 1);
      }
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [
    planogramResp,
    fitContent,
    initialProductId,
    effectiveZoomToProductIds,
    pulseOnFrame,
    resetViewNonce,
  ]);

  return (
    <>
      {pulseOnFrame && (
        <style>{`@keyframes plano-pulse-ring {
  0%   { box-shadow: 0 0 0 0 rgba(239,68,68,0.75); }
  70%  { box-shadow: 0 0 0 12px rgba(239,68,68,0); }
  100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
}`}</style>
      )}
      <div
        ref={containerRef}
        id="planogram-view-container"
        className="relative flex h-[400px] items-center justify-center border border-gray-400 bg-gray-200"
      >
        {!planogramResp && (
          <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
            <div className="animate-spin h-8 w-8 border-4 border-gray-400 border-t-transparent rounded-full" />
          </div>
        )}
        {!!planogramResp && (
          <>
            <TransformWrapper
              ref={transformRef}
              initialScale={1}
              minScale={1}
              maxScale={Infinity}
              pinch={{ step: 5 }}
              doubleClick={{ disabled: true }}
              panning={{ velocityDisabled: true }}
              onTransformed={handleTransformed}
            >
              <TransformSync onStateChange={handleTransformChange} />
              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%" }}
                contentStyle={{ width: "100%", height: "100%" }}
              >
                <PlanoLabelBoxes
                  planogram={planogramResp}
                  selectedProductIds={selectedProductIds}
                  warnedProductIds={warnedProductIds}
                  highlightedProductIds={highlightedProductIds}
                  currentScale={currentScale}
                  imageUrlOverrides={imageUrlOverrides}
                  tooltipImageUrlOverrides={tooltipImageUrlOverrides}
                  backgroundImageUrl={backgroundImageUrl}
                  searchMatchedIds={searchMatchedIds}
                  pulseNonce={pulseOnFrame ? pulseNonce : undefined}
                  pulseProductIds={
                    pulseOnFrame ? effectiveZoomToProductIds : undefined
                  }
                />
              </TransformComponent>
            </TransformWrapper>

            {!!activePlanogramProductId?.length && !hidePopover && (
              <ActivePlanoProductPopover
                enable_product_id_copy={enable_product_id_copy}
                planogram_id={planogramResp.id || ""}
                activePlanogramProductId={activePlanogramProductId}
              />
            )}
          </>
        )}

        {!!planogramResp && (
          <PlanogramSearch
            value={searchQuery}
            onChange={setSearchQuery}
            matchCount={searchMatchedIds?.size}
          />
        )}

        {currentScale > 1 && containerSize.width > 0 && planogramResp && (
          <PlanogramMinimap
            planogramProducts={planogramResp.products ?? []}
            transformState={transformState}
            containerSize={containerSize}
            onPan={handleMinimapPan}
          />
        )}
      </div>
    </>
  );
};
